// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const soroContainerRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Blog posts — Sales-focused, conversion-driven content (rewritten 2026-08-22)
  // Anchored on the £1.99 Premium value proposition + a dedicated fuel post.
  const placeholderPosts = [
    {
      id: 1,
      title: "Why £1.99/month PricePAL Premium Pays for Itself in Under a Week",
      excerpt: "The average UK household spends £5,400/year on groceries. Save just 5% and you're up hundreds. Here's the maths on why £1.99/month is the cheapest bill you'll ever pay for itself.",
      content: `
# Why £1.99/month PricePAL Premium Pays for Itself in Under a Week

If your weekly grocery shop costs £100 and you save just 5% by comparing prices before you buy, that's **£5 back in your pocket every week**. £1.99 for the whole month? You're up £3 on your first Sunday.

But most UK households don't compare properly. Prices change weekly. Own-brand vs. branded, offers vs. multi-buys — the same tin of beans is 47p in one aisle and 89p at the store next door. The maths against you is quiet, small, and constant.

## The £1.99 Break-Even Table

| Weekly grocery spend | 5% saving | Time to break even on £1.99 |
|---|---|---|
| £60 | £3.00 | Under 1 week |
| £100 | £5.00 | 3 days |
| £150 | £7.50 | 2 days |
| £200 | £10.00 | Under 2 days |

That's before we get to fuel savings. That's before Kitchen AI plans your meals around this week's offers. That's before price alerts ping your phone the moment your regular pack of coffee drops below your target.

## What You Actually Get for £1.99

- **Unlimited barcode scans** — compare Tesco, Sainsbury's, Asda, Morrisons, Aldi, Lidl, Waitrose and Co-op in seconds
- **Unlimited price alerts** — set a target price on the items you buy weekly and get pinged when any UK retailer matches
- **Full Fuel Finder** — postcode search + live map of the cheapest petrol and diesel near you
- **Kitchen AI meal planner** — AI-suggested weekly meal plans built around this week's offers
- **AI receipt scanning** — snap a supermarket receipt; we itemise and re-price it across every rival supermarket automatically
- **Personal Savings Tracker** — see exactly how much you've saved this month, this year, and lifetime

## Family and Community Options

**Family Plan — £2.99/month** covers up to 4 members on one bill (cheaper than two solo Premiums).

**Community Plan — £1.00/month** for NHS staff, Blue Light Card holders, students (NUS/TOTUM) and qualified teachers (TRN) after a quick one-time ID verification at [/community](/community).

## 7-Day Free Trial — Card Required

Every new subscriber gets 7 days of Premium free. A valid card is required at sign-up (Stripe holds it securely — you are not charged during the trial). Cancel any time before day 7 and you won't be charged.

If you save even £2 on your first shop, Premium has already paid for itself.

[Start your 7-day Premium trial →](/register)

---

*PricePAL Compare Ltd is registered in England and Wales (Company #17031232). Individual savings will vary based on shopping habits. Figures based on ONS Consumer Price Inflation data (2025).*
      `,
      author: "PricePAL Compare Team",
      date: "2026-08-15",
      category: "Value & Savings",
      image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=400&fit=crop",
      readTime: "5 min read"
    },
    {
      id: 2,
      title: "UK Petrol Prices: Why You're Paying 10p a Litre Too Much (And How to Stop Today)",
      excerpt: "RAC Fuel Watch data shows the gap between the cheapest and most expensive UK stations regularly hits 10-12p per litre in the same postcode. Here's how to stop overpaying — starting your next fill-up.",
      content: `
# UK Petrol Prices: Why You're Paying 10p a Litre Too Much (And How to Stop Today)

There is no such thing as a "market price" for UK fuel. Every station sets its own price. Every day. And the difference between the cheapest and most expensive petrol pump within a five-mile radius of your home regularly hits **10p per litre** — sometimes 12p or more, according to RAC Fuel Watch.

For a 50-litre tank that's a **£6 penalty** for pulling into the wrong pump. For anyone who fills up weekly, that quietly adds up to **over £300 a year** — per driver, per household.

## Why the Gap Is So Big

- **Rural stations** on trunk roads charge more because drivers have no alternative
- **Motorway service stations** are the most expensive in the country — often 20p+ per litre above the average
- **Supermarket forecourts** (Tesco, Asda, Sainsbury's, Morrisons) are usually cheapest — but only if you have one competing locally
- **Independent stations** vary wildly. Some undercut supermarkets by 3–5p; others price identically to motorway sites.

The only way to know where you actually stand is to compare — every time — before you pull off the road.

## How PricePAL Compare Fuel Finder Works

Open the Fuel tab, allow location (or enter a postcode) and you get a live map of every station within your radius, sorted by:

- Cheapest petrol
- Cheapest diesel
- Distance from you
- Brand + last-updated timestamp

Data comes from the CMA's official fuel price transparency scheme (Tesco, Asda, Sainsbury's, Morrisons, Applegreen, Ascona and others), refreshed hourly, layered with real-time community reports from other PricePAL Compare drivers.

## The Real-World Maths

Say you drive **12,000 miles a year in a car doing 40 mpg**. That works out to roughly 60 fills at 22 litres each.

- Overpay by **5p/litre**: £66/year wasted
- Overpay by **8p/litre**: £106/year wasted
- Overpay by **12p/litre**: £158/year wasted

PricePAL Compare Premium is **£1.99/month (£23.88/year)** or £19.99 on annual billing. Every mile you drive on the cheaper pump is money back in your pocket.

## Bonus: You Also Get Grocery Comparison + Kitchen AI

The Fuel Finder is one feature. For the same £1.99/month you also get every product comparison across the UK's major supermarkets, unlimited price alerts, AI receipt scanning, Kitchen AI meal planning, and the personal Savings Tracker.

Households where both partners drive typically break even on Premium in the first tank alone.

## Ready to Stop Overpaying?

Start your 7-day free trial today. Card required at sign-up (Stripe secure); cancel any time before day 7 for zero charge.

- **Premium** — £1.99/month
- **Family Plan** — £2.99/month (up to 4 members)
- **Community Plan** — £1.00/month (NHS · Blue Light · Students · Teachers)

[Find cheaper fuel now — start 7-day trial →](/register)

---

*Sources: RAC Fuel Watch, CMA fuel price transparency scheme. Prices refresh hourly; individual savings will vary by location and driving habits. PricePAL Compare Ltd, Company #17031232.*
      `,
      author: "PricePAL Compare Team",
      date: "2026-08-18",
      category: "Fuel Prices",
      image: "https://images.unsplash.com/photo-1545262810-77515befe149?w=800&h=400&fit=crop",
      readTime: "6 min read"
    },
    {
      id: 3,
      title: "The 3-Minute Weekly Habit That Cuts £1,000+ Off Your UK Grocery Bill",
      excerpt: "The single behaviour change that separates households who save £1,000/year on food from ones who don't. It takes three minutes on your Sunday sofa — and it costs £1.99/month.",
      content: `
# The 3-Minute Weekly Habit That Cuts £1,000+ Off Your UK Grocery Bill

There is one behaviour that separates the households who quietly save £1,000+ a year on food from the ones who don't: **they know the price of what they buy before they buy it.**

Not roughly. Not "Tesco is usually cheap." Actually. Every week. In under three minutes.

## The Habit, Step by Step

Sunday evening. Six items on your regular weekly list.

1. Open PricePAL Compare
2. Search each item — milk, bread, coffee, eggs, chicken, washing detergent — see which supermarket is cheapest **this week**
3. Set a price alert for two items you buy least often (stock up when they drop below your target)
4. Copy the winners to your Kitchen AI weekly plan
5. Screenshot your list, do the shop

Total time: about three minutes. Compounded over 52 weeks: roughly **£800–£1,500 back in your account**.

## Why It Works

Supermarkets rotate promotions on a **6–8 week cycle**. What is £3.50 at Tesco this week will be £2.00 at Asda in three weeks. If you don't know when to pounce, you pay the average of both prices all year. If you do, you always pay the lower.

The people who "always shop at Aldi" leave money on the table by ignoring the branded items that are genuinely cheaper elsewhere. The people who "shop at the closest supermarket" are being penalised for their convenience by 15–25%.

## Kitchen AI Turns the Habit into Auto-Pilot

For £1.99/month, Kitchen AI:

- Reads what's cheap this week across all 8 major UK retailers
- Suggests recipes that use those items
- Builds you a full weekly meal plan you can copy into your shopping list
- Learns your dietary preferences (no beef, halal, vegetarian, gluten-free, etc.)

You get the savings without doing the mental work.

## The Numbers

The average UK household spends around **£5,400 a year** on groceries (ONS Consumer Price Inflation, 2025). Even a **15% saving via disciplined price comparison delivers £810/year**. A 20% saving delivers **£1,080/year**.

PricePAL Compare Premium is **£23.88/year (£1.99/month)**. Family Plan (up to 4 members) is **£35.88/year (£2.99/month)**.

The return on that investment is not close.

## What You Pay, What You Get

| Plan | Price | For |
|---|---|---|
| **Premium** | £1.99/mo | Single-adult household. Unlimited scans, alerts, Kitchen AI, Fuel Finder, receipts, savings tracker. |
| **Family Plan** | £2.99/mo | Up to 4 members on one bill. Perfect for busy families. |
| **Community Plan** | £1.00/mo | NHS staff · Blue Light Card · Students (NUS/TOTUM) · Teachers (TRN). One-time ID check. |

Every new subscriber gets a **7-day free trial**. Card required at sign-up (Stripe secure); cancel any time before day 7 for zero charge.

[Start saving £1,000+ this year →](/register)

---

*Sources: ONS Consumer Price Inflation dataset (2025). Individual savings will vary. PricePAL Compare Ltd is registered in England and Wales, Company #17031232.*
      `,
      author: "PricePAL Compare Team",
      date: "2026-08-20",
      category: "Money-Saving Habits",
      image: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&h=400&fit=crop",
      readTime: "5 min read"
    }
  ];

  useEffect(() => {
    // Try to fetch posts from database first, fall back to placeholder posts
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/blog/posts`);
        if (response.ok) {
          const data = await response.json();
          if (data.posts && data.posts.length > 0) {
            setPosts(data.posts);
            return;
          }
        }
      } catch (error) {
      }
      // Fall back to placeholder posts if API fails or returns empty
      setPosts(placeholderPosts);
    };
    fetchPosts();
  }, []);

  // iter172d (2026-08-22) — Load the Soro AI marketing blog embed only
  // on the list view (not when a curated post is expanded). Soro's script
  // hydrates any <div id="soro-blog"></div> it finds on the page.
  useEffect(() => {
    if (selectedPost) return;  // hide embed on individual-post view
    if (document.getElementById('soro-embed-script')) return;  // already loaded
    const script = document.createElement('script');
    script.id = 'soro-embed-script';
    script.src = 'https://app.trysoro.com/api/embed/5cf257b3-d3dd-4dbe-bf86-564b25679983';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      // Clean-up if user navigates away — allow re-hydration next visit.
      const s = document.getElementById('soro-embed-script');
      if (s) s.remove();
      const target = document.getElementById('soro-blog');
      if (target) target.innerHTML = '';
    };
  }, [selectedPost]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 hover:opacity-80 transition"
            >
              <span className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                <span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span>
              </span>
            </button>
            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => navigate('/')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Home</button>
              <button onClick={() => navigate('/pricing')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Pricing</button>
              <button onClick={() => navigate('/blog')} className="text-blue-600 font-bold">Blog</button>
              {/* Founder's Club nav button hidden 2026-06 — beta tiers no longer surfaced in public navigation
              <button onClick={() => navigate('/pricing')} className="text-gray-700 hover:text-blue-600 font-semibold transition">Founder's Club</button>
              */}
              <button onClick={() => navigate('/app')} className="px-6 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white font-bold rounded-lg hover:shadow-lg transition">Sign In</button>
            </div>
            <div className="flex md:hidden items-center gap-3">
              <button onClick={() => navigate('/app')} className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white text-sm font-bold rounded-lg">Sign In</button>
              <button onClick={() => navigate('/')} className="text-sm text-gray-500 hover:text-blue-600 font-semibold whitespace-nowrap">← Home</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Blog Content */}
      {selectedPost ? (
        // Individual Blog Post View
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => setSelectedPost(null)}
            className="mb-6 text-blue-600 hover:text-blue-800 font-semibold flex items-center"
          >
            ← Back to Blog
          </button>

          <article className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img
              src={selectedPost.image}
              alt={selectedPost.title}
              className="w-full h-96 object-cover"
            />
            
            <div className="p-8">
              <div className="flex items-center space-x-4 mb-4 text-sm text-gray-500">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                  {selectedPost.category}
                </span>
                <span>{selectedPost.date}</span>
                <span>•</span>
                <span>{selectedPost.readTime}</span>
              </div>

              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {selectedPost.title}
              </h1>

              <div className="flex items-center space-x-3 mb-8 pb-8 border-b">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {selectedPost.author.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{selectedPost.author}</div>
                  <div className="text-sm text-gray-500">PricePAL Compare Team</div>
                </div>
              </div>

              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                  {selectedPost.content}
                </div>
              </div>

              <div className="mt-12 pt-8 border-t">
                <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl p-8 text-white text-center">
                  <h3 className="text-2xl font-bold mb-3">
                    Start Saving Like Our Users
                  </h3>
                  <p className="mb-6 text-blue-100">
                    Join UK shoppers building the community price comparison database
                  </p>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:shadow-xl transition"
                  >
                    Start 7-Day Trial — Card required
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>
      ) : (
        // Blog List View
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              PricePAL Compare Blog
            </h1>
            <p className="text-xl text-gray-600">
              Money-saving tips, success stories, and UK grocery & fuel market insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105 cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover"
                />
                
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      {post.category}
                    </span>
                    <span className="text-xs text-gray-500">{post.readTime}</span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      {post.date}
                    </div>
                    <button className="text-blue-600 font-semibold hover:text-blue-800 flex items-center">
                      Read More →
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Soro AI Marketing embed — auto-refreshing SEO content below curated posts */}
          <section className="mt-20 pt-12 border-t border-gray-200" data-testid="soro-blog-section">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">More Money-Saving Insights</h2>
              <p className="text-gray-600">Fresh weekly tips on UK grocery savings, fuel comparisons, and shopping smarter.</p>
            </div>
            <div id="soro-blog" ref={soroContainerRef} data-testid="soro-blog-embed" />
          </section>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
